import { z } from "zod";
import { conferirMultipla, zConteudoCartao, zConteudoCertoErrado, zConteudoCloze, zConteudoMultipla, zGabaritoCartao, zGabaritoCertoErrado, zGabaritoCloze, zGabaritoMultipla, } from "./dominio.js";
/**
 * O formato de importação da seção 5 do SPEC.
 *
 * Ele nasce agora, na etapa 2, junto do conteúdo de exemplo — e não na etapa 4
 * com o importador. O motivo é que o arquivo de exemplo precisa estar no
 * formato final desde o primeiro dia: se ele nascer num formato "de teste", o
 * importador vai ser escrito contra um corpo que ninguém importa de verdade.
 *
 * O que **não** está aqui é a validação de contexto do importador (RN-21 a
 * RN-25): colisão de ids com o que já existe no banco, deslocamento para a
 * próxima faixa livre, avisos não bloqueantes. Isso depende do banco e é a
 * etapa 4. Aqui é só a forma do arquivo.
 */
/** O caminho na árvore de categorias: `["Modelagem", "Normalização"]`. */
export const zCaminhoCategoria = z
    .array(z.string().trim().min(1, "Nível de categoria não pode ser vazio"))
    .min(1, "Todo item precisa de ao menos um nível de categoria");
/** Chave de texto-base, como aparece em `textosBase` e no item que o usa. */
export const zChaveTextoBase = z.string().trim().min(1).max(64);
const zBaseItem = z.object({
    /**
     * Número dentro do lote, não identificador global. É o que permite excluir um
     * lote em bloco por faixa (RN-23) e reimportar o mesmo arquivo sem duplicar.
     */
    id: z.number().int().positive("O id do item é um inteiro positivo"),
    categoria: zCaminhoCategoria,
    /** Texto livre de propósito: quem importa decide a escala ("Fácil", "3", "TRF"). */
    dificuldade: z.string().trim().min(1).max(40).optional(),
    enunciado: z.string().trim().min(1, "Item sem enunciado"),
    explicacao: z.string().trim().min(1).optional(),
    textoBase: zChaveTextoBase.optional(),
});
export const zItemImportado = z
    .discriminatedUnion("tipo", [
    zBaseItem.extend({ tipo: z.literal("multipla"), conteudo: zConteudoMultipla, gabarito: zGabaritoMultipla }),
    zBaseItem.extend({ tipo: z.literal("certo_errado"), conteudo: zConteudoCertoErrado, gabarito: zGabaritoCertoErrado }),
    zBaseItem.extend({ tipo: z.literal("cartao"), conteudo: zConteudoCartao, gabarito: zGabaritoCartao }),
    zBaseItem.extend({ tipo: z.literal("cloze"), conteudo: zConteudoCloze, gabarito: zGabaritoCloze }),
])
    .superRefine((item, ctx) => conferirMultipla(item, ctx));
export const zArquivoImportacao = z
    .object({
    /**
     * Versão do formato, não do conteúdo. Existe para que um arquivo salvo hoje
     * possa ser recusado com uma frase útil quando o formato mudar, em vez de
     * falhar num campo qualquer.
     */
    versao: z.literal(1, "Formato desconhecido: esperava versao 1"),
    trilha: z.string().trim().min(1),
    lote: z.string().trim().min(1),
    /** De onde veio: arquivo, PDF, conversa. Vira `lote.origem`. */
    fonte: z.string().trim().min(1).optional(),
    textosBase: z.record(zChaveTextoBase, z.string().trim().min(1)).default({}),
    itens: z.array(zItemImportado).min(1, "O arquivo não tem nenhum item"),
})
    .superRefine((arquivo, ctx) => {
    // Dois itens com o mesmo id dentro do arquivo: o segundo sobrescreveria o
    // primeiro em silêncio na hora de gravar a faixa do lote.
    const vistos = new Map();
    arquivo.itens.forEach((item, i) => {
        const antes = vistos.get(item.id);
        if (antes !== undefined) {
            ctx.addIssue({
                code: "custom",
                message: `Id ${item.id} repetido: já usado pelo item na posição ${antes + 1}`,
                path: ["itens", i, "id"],
            });
        }
        else {
            vistos.set(item.id, i);
        }
    });
    // Item que aponta para um texto-base inexistente perderia o texto sem aviso,
    // e um enunciado que depende de texto some do sentido sem ele.
    const chaves = new Set(Object.keys(arquivo.textosBase));
    arquivo.itens.forEach((item, i) => {
        if (item.textoBase && !chaves.has(item.textoBase)) {
            ctx.addIssue({
                code: "custom",
                message: `Texto-base "${item.textoBase}" não existe em textosBase`,
                path: ["itens", i, "textoBase"],
            });
        }
    });
});
/** A faixa de ids do arquivo — é o que o lote guarda para excluir em bloco (RN-23). */
export function faixaDeIds(itens) {
    const ids = itens.map((i) => i.id);
    return { inicio: Math.min(...ids), fim: Math.max(...ids) };
}
/**
 * Os caminhos de categoria distintos do arquivo, com todos os níveis
 * intermediários. `["A","B"]` produz `["A"]` e `["A","B"]`, porque a árvore
 * precisa do pai antes do filho e o arquivo não é obrigado a citá-lo sozinho.
 */
export function caminhosDeCategoria(itens) {
    const vistos = new Map();
    for (const item of itens) {
        for (let i = 1; i <= item.categoria.length; i++) {
            const caminho = item.categoria.slice(0, i);
            vistos.set(caminho.join(" "), caminho);
        }
    }
    // Rasos primeiro: garante que o pai já existe quando o filho for criado.
    return [...vistos.values()].sort((a, b) => a.length - b.length || a.join().localeCompare(b.join()));
}
//# sourceMappingURL=importacao.js.map